/**
 * @fileoverview `@puffin-core/error-handling` — the English (EN) bundle, namespace #57 `errorHandling`.
 * @package @puffin-core/error-handling
 *
 * The package's words live HERE, in the ESM-only `./strings` leaf, and only here. The CommonJS half carries none:
 * the error-to-notification mapper builds its title from a KEY into this catalog, translated by the translator its
 * host hands it (THINKPAD's #57 converge ruling, option (D), 2026-10-23).
 *
 * `notifications.*` moved VERBATIM from pos-web's `errors.notifications.errorTitle.*` and
 * `errors.notifications.errorTitleInComponent` when the mapper did (D1 errorCode unit 4), both locales with their review
 * flags. `notifications.messageUnavailable` is the mapper's own fallback message, which had been an English literal in
 * both halves of this package.
 *
 * @module @puffin-core/error-handling/strings/en
 */

export const ERROR_HANDLING_EN = Object.freeze({
  notifications: Object.freeze({
    errorTitle: Object.freeze({
      system: 'System Error',
      ui: 'UI Error',
      api: 'API Error',
      database: 'Database Error',
      validation: 'Validation Error',
      network: 'Network Error',
      security: 'Security Error',
      businessRule: 'Business Rule Error',
      performance: 'Performance Issue',
      unknown: 'Error',
    }),
    // The whole sentence is a key, never a concatenation: a language with another word order is served by the catalog.
    errorTitleInComponent: '{{title}} in {{component}}',
    messageUnavailable: 'Unknown error occurred',
  }),
  // `formatApiErrorForDisplay`'s slots, moved VERBATIM from both halves of the handler when it stopped composing words
  // (#57). A host shows the server's own sentence when there is one, else `message`, under `title`.
  display: Object.freeze({
    network: Object.freeze({
      title: 'Connection Error',
      message: 'Unable to connect to the server. Please check your internet connection.',
    }),
    timeout: Object.freeze({
      title: 'Request Timeout',
      message: 'The request took too long to complete. Please try again.',
    }),
    authentication: Object.freeze({
      title: 'Authentication Required',
      message: 'Please log in to continue.',
    }),
    forbidden: Object.freeze({
      title: 'Access Denied',
      message: 'You do not have permission to perform this action.',
    }),
    notFound: Object.freeze({
      title: 'Not Found',
      message: 'The requested resource was not found.',
    }),
    validation: Object.freeze({
      title: 'Validation Error',
      message: 'Please check your input and try again.',
    }),
    server: Object.freeze({
      title: 'Server Error',
      message: 'An unexpected server error occurred. Please try again later.',
    }),
    unknown: Object.freeze({
      title: 'Error',
      message: 'An error occurred. Please try again.',
    }),
  }),
  // The words of every rule a 422 detail may carry (PPUR 5d server half, D-PPUR-25), keyed by the rule itself:
  // `@puffin-core/response-helpers` VALIDATION_RULES, judged in both directions by `validationRulesWorded.contract`.
  // {{field}} is the field's label; {{limit}}, {{valids}} and {{name}} are the detail's whitelisted params.
  validationRule: Object.freeze({
    'actions.none': 'At least one action is required.',
    'address.country': '{{field}} names no country.',
    'address.countryCodeNotAllowed': '{{field}} is not a country an address may be entered in.',
    'address.invalid': 'The address is incomplete or not valid.',
    'address.postalCodeInvalidForCountry': '{{field}} is not a valid postal code for the selected country.',
    'address.stateCodeCountryMismatch': '{{field}} does not belong to the selected country.',
    'alternatives.all': '{{field}} does not meet every requirement.',
    'alternatives.any': '{{field}} does not match any allowed form.',
    'alternatives.match': '{{field}} does not match any allowed form.',
    'alternatives.one': '{{field}} matches more than one allowed form.',
    'alternatives.types': '{{field}} is not one of the allowed types.',
    'any.custom': '{{field}} is not valid.',
    'any.default': '{{field}} could not be given its default value.',
    'any.failover': '{{field}} could not be given its fallback value.',
    'any.invalid': '{{field}} is not an allowed value.',
    'any.only': '{{field}} must be one of: {{valids}}.',
    'any.ref': '{{field}} refers to a value that is not valid.',
    'any.required': '{{field}} is required.',
    'any.ruleDate': '{{field}} must be a real calendar date.',
    'any.unknown': '{{field}} is not allowed here.',
    'area.pastTheEdge': '{{field}} reaches past the edge of the area.',
    'array.base': '{{field}} must be a list.',
    'array.excludes': '{{field}} contains an item that is not allowed.',
    'array.hasKnown': '{{field}} is missing a required item.',
    'array.hasUnknown': '{{field}} is missing a required item.',
    'array.includes': '{{field}} contains an item that is not valid.',
    'array.includesRequiredBoth': '{{field}} is missing required items.',
    'array.includesRequiredKnowns': '{{field}} is missing required items.',
    'array.includesRequiredUnknowns': '{{field}} is missing required items.',
    'array.length': '{{field}} must have exactly {{limit}} items.',
    'array.max': '{{field}} may have at most {{limit}} items.',
    'array.min': '{{field}} must have at least {{limit}} items.',
    'array.orderedLength': '{{field}} must have at most {{limit}} items.',
    'array.sort': '{{field}} is not in the required order.',
    'array.sort.mismatching': '{{field}} cannot be sorted: its items differ in kind.',
    'array.sort.unsupported': '{{field}} cannot be sorted this way.',
    'array.sparse': '{{field}} must not have empty items.',
    'array.unique': '{{field}} has an item more than once.',
    'band.belowLower': '{{field}} must not be below the band beneath it.',
    'boolean.base': '{{field}} must be yes or no.',
    'branding.notInLibrary': '{{field}} must be an image from the site’s media library.',
    'branding.toneUnreadable': '{{field}} is too hard to read on its background.',
    'branding.tonesNeedBase': '{{field}} needs both the paper and ink colours set first.',
    'branding.unreadable': '{{field}} is too hard to read on the paper colour.',
    'captions.multipleDefaults': 'At most one caption may be the default.',
    'charge.quote': '{{field}} must be the price you were shown.',
    'contact.invalid': '{{field}} must be an email address or a phone number.',
    'content.date': '{{field}} must be a real calendar day.',
    'content.destination': '{{field}} must be a web address or a page on this site.',
    'content.href': '{{field}} must be a web address or a page on this site.',
    'content.key': '{{field}} must be lowercase words joined by hyphens, starting with a letter.',
    'date.base': '{{field}} must be a valid date.',
    'date.format': '{{field}} is not in the expected date format.',
    'date.format.iso': '{{field}} must be an ISO date.',
    'date.format.javascript': '{{field}} must be a timestamp.',
    'date.format.unix': '{{field}} must be a Unix timestamp.',
    'date.greater': '{{field}} must be later than the allowed date.',
    'date.less': '{{field}} must be earlier than the allowed date.',
    'date.max': '{{field}} must not be later than the allowed date.',
    'date.min': '{{field}} must not be earlier than the allowed date.',
    'countDenominations.sum': '{{field}} must add up to exactly the amount counted.',
    'departmentPlan.groupIdentity': '{{field}} must name an existing group or a new one, not both.',
    'departmentPlan.groupMismatch': '{{field}} names a group that is not in the plan.',
    'departmentPlan.groupProvenance': '{{field}} does not match how the group was created.',
    'departmentPlan.memberAmountUnknown': '{{field}} names a department that is not in the group.',
    'departmentPlan.memberDuplicate': 'A department may belong to only one group.',
    'duration.defaultOutOfRange': '{{field}} must be between the minimum and maximum durations.',
    'duration.minAboveMax': '{{field}} cannot be more than the maximum duration.',
    'field.defaultValueNotText': '{{field}} applies only to a text field.',
    'field.defaultValueTooLong': '{{field}} must not be longer than the field’s maximum length.',
    'field.maxLengthNotText': '{{field}} applies only to a text field.',
    'field.placeholderNotText': '{{field}} applies only to a text field.',
    'field.rowsNotTextarea': '{{field}} applies only to a multi-line text field.',
    'filters.json': '{{field}} must be a set of filters.',
    'function.arity': '{{field}} must take exactly {{limit}} arguments.',
    'function.class': '{{field}} must be a class.',
    'function.maxArity': '{{field}} may take at most {{limit}} arguments.',
    'function.minArity': '{{field}} must take at least {{limit}} arguments.',
    'garnishment.agencyUnknown':
      "This state's child support agency could not be read, so the order cannot be sent.",
    'garnishment.fipsNotListed': "This county code is not one the state's agency lists.",
    'garnishment.invalidPercentage': 'Enter a percentage above 0 and no more than 100.',
    'garnishment.overFederalCeiling': 'This is more than federal law allows to be withheld.',
    'garnishment.overStateCeiling': "This is more than the employee's work-state law allows to be withheld.",
    'garnishment.payPeriodExceedsAnnual':
      'The pay-period maximum cannot be more than the annual maximum.',
    'garnishment.requiredAttributeMissing': "The state's agency requires this number.",
    'garnishment.stateBarsOrder': "The employee's work-state law doesn't allow this kind of order against wages.",
    'jurisdiction.code': '{{field}} is not a jurisdiction.',
    'jurisdiction.parentNotAState': '{{field}} must be a known state.',
    'leaveType.needsBalance': '{{field}} needs the leave type to count against a balance.',
    'lifecycle.order': '{{field}} cannot be earlier than the opening date.',
    'location.notAuthorized': '{{field}} is not one of your authorized locations.',
    'lyWindow.order': '{{field}} must not start after it ends.',
    'measurement.incomplete':
      '{{field}} must be given with the rest of its measure: a weight with its unit, or length, width and height with their unit.',
    'metrics.unknown': '{{field}} names a metric this report does not have.',
    'mfaPolicy.optOutEnabled': '{{field}} cannot be opted out while it is enabled.',
    'mfaPolicy.optOutRequired': '{{field}} is required and cannot be opted out.',
    'navigation.url': '{{field}} must be a web address, an email link or a page on this site.',
    'number.base': '{{field}} must be a number.',
    'number.greater': '{{field}} must be greater than {{limit}}.',
    'number.infinity': '{{field}} must be a finite number.',
    'number.integer': '{{field}} must be a whole number.',
    'number.less': '{{field}} must be less than {{limit}}.',
    'number.max': '{{field}} must be at most {{limit}}.',
    'number.min': '{{field}} must be at least {{limit}}.',
    'number.multiple': '{{field}} must be a multiple of {{limit}}.',
    'number.negative': '{{field}} must be a negative number.',
    'number.port': '{{field}} must be a port number.',
    'number.positive': '{{field}} must be a positive number.',
    'number.precision': '{{field}} may have at most {{limit}} decimal places.',
    'number.unsafe': '{{field}} is too large to store exactly.',
    'object.and': '{{field}} must be given together with its related fields.',
    'object.assert': '{{field}} does not meet its rule.',
    'object.base': '{{field}} must be a set of values.',
    'object.instance': '{{field}} is not of the expected kind.',
    'object.length': '{{field}} must have exactly {{limit}} values.',
    'object.max': '{{field}} may have at most {{limit}} values.',
    'object.min': '{{field}} must have at least {{limit}} values.',
    'object.missing': 'At least one of the required fields must be given.',
    'object.nand': '{{field}} cannot be given with all of its related fields.',
    'object.oxor': 'Only one of these fields may be given.',
    'object.pattern.match': '{{field}} has a value that is not valid.',
    'object.refType': '{{field}} must refer to a value.',
    'object.regex': '{{field}} must be a pattern.',
    'object.rename.multiple': '{{field}} was given more than once.',
    'object.rename.override': '{{field}} was given more than once.',
    'object.rename.proto': '{{field}} is not allowed here.',
    'object.schema': '{{field}} must be a schema.',
    'object.unknown': '{{field}} is not allowed here.',
    'object.with': '{{field}} needs its related field as well.',
    'object.without': '{{field}} cannot be given with a related field.',
    'object.xor': 'Exactly one of these fields must be given.',
    'operatingHours.week': 'The weekly hours are not valid.',
    'parameters.undeclared': '{{field}} has a setting this rule does not take.',
    'permission.coveredByGrant':
      '{{field}} is still granted by a broader permission: revoke that grant instead.',
    'permission.notDelegatable': '{{field}} is a permission you do not hold, so you cannot grant it.',
    'permission.pairIncoherent': '{{field}} needs its view permission: grant both, or revoke both.',
    'permission.unknown': '{{field}} is not a known permission.',
    'phone.notTextable': '{{field}} cannot receive a text message: use a mobile number.',
    'placeId.shape': '{{field}} must be a Google Business Profile Place ID.',
    'placeId.text': '{{field}} must be text.',
    'product.departmentRequired': '{{field}} follows the product’s department, so choose a department.',
    'product.notApplicable': '{{field}} does not apply to the product as it is set.',
    'product.weightNeededToShip': '{{field}} is required for a product that ships.',
    'range.reversed': '{{field}} must not be before the start date.',
    'range.tooLong': 'The date range is too long.',
    'reportDefinition.createOnly': '{{field}} cannot be changed after the report is created.',
    'roleLimit.notDelegatable': '{{field}} is above your own limit of {{limit}}, so you cannot set it.',
    'setting.order': '{{field}} must be at most {{limit}}, the setting it may not exceed.',
    'signatureStrokes.invalid': '{{field}} is not a signature.',
    'siteLink.url': '{{field}} must be a web address, an email link or a page on this site.',
    'span.reversed': '{{field}} must not be before the start.',
    'span.tooLong': 'The span is too long.',
    'string.alphanum': '{{field}} may contain only letters and digits.',
    'string.base': '{{field}} must be text.',
    'string.base64': '{{field}} must be base64 text.',
    'string.civilDate': '{{field}} must be a real calendar date.',
    'string.creditCard': '{{field}} must be a card number.',
    'string.dataUri': '{{field}} must be a data URI.',
    'string.domain': '{{field}} must be a domain name.',
    'string.email': '{{field}} must be an email address.',
    'string.empty': '{{field}} must not be empty.',
    'string.guid': '{{field}} must be a GUID.',
    'string.hex': '{{field}} must be hexadecimal.',
    'string.hexAlign': '{{field}} must have an even number of hexadecimal digits.',
    'string.hostname': '{{field}} must be a host name.',
    'string.ip': '{{field}} must be an IP address.',
    'string.ipVersion': '{{field}} must be an IP address of an allowed version.',
    'string.isoDate': '{{field}} must be an ISO date.',
    'string.isoDuration': '{{field}} must be an ISO duration.',
    'string.length': '{{field}} must be exactly {{limit}} characters.',
    'string.lowercase': '{{field}} must be lowercase.',
    'string.max': '{{field}} may be at most {{limit}} characters.',
    'string.min': '{{field}} must be at least {{limit}} characters.',
    'string.normalize': '{{field}} must be normalized Unicode text.',
    'string.pattern.base': '{{field}} is not in the expected format.',
    'string.pattern.invert.base': '{{field}} is in a format that is not allowed.',
    'string.pattern.invert.name': '{{field}} must not be a {{name}}.',
    'string.pattern.name': '{{field}} must be a {{name}}.',
    'string.semver': '{{field}} must be a version number.',
    'string.timeZone': '{{field}} must be a time zone.',
    'string.token': '{{field}} may contain only letters, digits and underscores.',
    'string.trim': '{{field}} must not begin or end with spaces.',
    'string.uppercase': '{{field}} must be uppercase.',
    'string.uri': '{{field}} must be a web address.',
    'string.uriCustomScheme': '{{field}} must be a web address of an allowed kind.',
    'string.uriRelativeOnly': '{{field}} must be a relative web address.',
    'string.wallClockTime': '{{field}} must be a time of day (HH:mm:ss).',
    'systemSeed.immutable': '{{field}} cannot be changed on a built-in type.',
    'tender.details': 'The payment details are not valid.',
    'timezone.iana': '{{field}} must be a time zone.',
    'upload.notAccepted': '{{field}} is not an accepted upload.',
    'user.active': '{{field}} is not an active user of this store.',
    'userIds.duplicate': '{{field}} names a Team Member more than once.',
    'userIds.malformed': '{{field}} must be a list of Team Members.',
    'userIds.tooMany': '{{field}} names too many Team Members.',
    'website.address': '{{field}} must be a web address.',
    'window.dayOutside': '{{field}} must fall inside the period.',
    'window.reversed': '{{field}} must not be before the start date.',
    'window.tooLong': 'The window may cover at most {{limit}} days.',
  }),
});

export default ERROR_HANDLING_EN;
